import { useCallback } from 'react';

import {
  EXCLUSIVE_PREFILL_CHOICES,
  PLAN_REQUIRED_PREFILL_FIELDS,
  formatMissingPlanFields,
  isAdvisorPrefillQuestionAllowed,
  shouldAskPartnerQuestions,
} from '@shared';
import type { AdvisorPrefillPolicy } from '@shared';

import { createPrefillDraftState, useInviteClientStore, type PrefillDraft } from '../store/store';
import { validatePrefillDraft } from './validate-prefill-draft';

export type { PrefillDraft } from '../store/store';

export type PrefillMultiSelectField = 'assetTypes' | 'legalDocuments' | 'supportNetwork';

const MULTI_SELECT_QUESTION_IDS: Record<PrefillMultiSelectField, string> = {
  assetTypes: 'financialAssetTypes',
  legalDocuments: 'legalDocuments',
  supportNetwork: 'clientProfessionalSupportNetworkList',
};

const PLAN_REQUIRED_DRAFT_FIELDS: Record<string, keyof PrefillDraft> = {
  clientBirthDate: 'birthDate',
  clientGenderAtBirth: 'genderAtBirth',
  clientZipCode: 'zipCode',
};

const DRAFT_FIELD_QUESTION_IDS: Record<keyof PrefillDraft, string> = {
  partnerFirstName: 'partnerFirstName',
  partnerLastName: 'partnerLastName',
  birthDate: 'clientBirthDate',
  zipCode: 'clientZipCode',
  genderAtBirth: 'clientGenderAtBirth',
  maritalStatus: 'clientMaritalStatus',
  householdSize: 'householdHouseholdSize',
  totalAssets: 'householdTotalAssetsValue',
  priorYearIncome: 'householdPriorYearIncome',
  assetTypes: 'financialAssetTypes',
  legalDocuments: 'legalDocuments',
  supportNetwork: 'clientProfessionalSupportNetworkList',
  highestDegree: 'clientHighestDegree',
};

function isDraftFieldAllowed(policy: AdvisorPrefillPolicy, field: keyof PrefillDraft): boolean {
  return isAdvisorPrefillQuestionAllowed(policy, DRAFT_FIELD_QUESTION_IDS[field]);
}

function filterDraftForPolicy(draft: PrefillDraft, policy: AdvisorPrefillPolicy): PrefillDraft {
  return {
    partnerFirstName: isDraftFieldAllowed(policy, 'partnerFirstName') ? draft.partnerFirstName : '',
    partnerLastName: isDraftFieldAllowed(policy, 'partnerLastName') ? draft.partnerLastName : '',
    birthDate: isDraftFieldAllowed(policy, 'birthDate') ? draft.birthDate : undefined,
    zipCode: isDraftFieldAllowed(policy, 'zipCode') ? draft.zipCode : '',
    genderAtBirth: isDraftFieldAllowed(policy, 'genderAtBirth') ? draft.genderAtBirth : '',
    maritalStatus: isDraftFieldAllowed(policy, 'maritalStatus') ? draft.maritalStatus : '',
    householdSize: isDraftFieldAllowed(policy, 'householdSize') ? draft.householdSize : '',
    totalAssets: isDraftFieldAllowed(policy, 'totalAssets') ? draft.totalAssets : '',
    priorYearIncome: isDraftFieldAllowed(policy, 'priorYearIncome') ? draft.priorYearIncome : '',
    assetTypes: isDraftFieldAllowed(policy, 'assetTypes') ? draft.assetTypes : [],
    legalDocuments: isDraftFieldAllowed(policy, 'legalDocuments') ? draft.legalDocuments : [],
    supportNetwork: isDraftFieldAllowed(policy, 'supportNetwork') ? draft.supportNetwork : [],
    highestDegree: isDraftFieldAllowed(policy, 'highestDegree') ? draft.highestDegree : '',
  };
}

function missingPlanFields(draft: PrefillDraft): string[] {
  return PLAN_REQUIRED_PREFILL_FIELDS.filter(questionId => {
    const value = draft[PLAN_REQUIRED_DRAFT_FIELDS[questionId]];
    return typeof value === 'string' ? value.trim().length === 0 : !value;
  });
}

function countPrefilledAnswers(draft: PrefillDraft, policy: AdvisorPrefillPolicy): number {
  const allowedDraft = filterDraftForPolicy(draft, policy);
  const textValues = [
    allowedDraft.partnerFirstName,
    allowedDraft.partnerLastName,
    allowedDraft.zipCode,
    allowedDraft.genderAtBirth,
    allowedDraft.maritalStatus,
    allowedDraft.householdSize,
    allowedDraft.totalAssets,
    allowedDraft.priorYearIncome,
    allowedDraft.highestDegree,
  ];
  const multiSelectValues = [
    allowedDraft.assetTypes,
    allowedDraft.legalDocuments,
    allowedDraft.supportNetwork,
  ];

  return (
    (allowedDraft.birthDate ? 1 : 0) +
    textValues.filter(value => value.trim().length > 0).length +
    multiSelectValues.filter(values => values.length > 0).length
  );
}

export function usePrefillDialog() {
  const {
    inviteClientConfig,
    setInviteClientConfig,
    prefillDraftState,
    setPrefillDraftState,
    showPrefillAnswers,
    setShowPrefillAnswers,
    advisorPrefillPolicy,
  } = useInviteClientStore();

  const draft = prefillDraftState.answers;
  const allowsMaritalStatus = isDraftFieldAllowed(advisorPrefillPolicy, 'maritalStatus');

  const savedDraft = createPrefillDraftState(inviteClientConfig).answers;
  const prefilledCount = countPrefilledAnswers(savedDraft, advisorPrefillPolicy);

  const draftMissingPlanFields = advisorPrefillPolicy.canCreateQuickPlan
    ? missingPlanFields(draft)
    : [];
  const savedMissingPlanFields = advisorPrefillPolicy.canCreateQuickPlan
    ? missingPlanFields(savedDraft)
    : [];

  const openDialog = useCallback(() => {
    setShowPrefillAnswers(true);
  }, [setShowPrefillAnswers]);

  const closeDialog = useCallback(() => {
    setShowPrefillAnswers(false);
  }, [setShowPrefillAnswers]);

  const cancelDialog = useCallback(() => {
    setPrefillDraftState(createPrefillDraftState(inviteClientConfig));
    setShowPrefillAnswers(false);
  }, [inviteClientConfig, setPrefillDraftState, setShowPrefillAnswers]);

  const errors = validatePrefillDraft(filterDraftForPolicy(draft, advisorPrefillPolicy));
  if (!prefillDraftState.isBirthDateInputValid) {
    errors.birthDate = 'Enter a valid date';
  }
  const hasErrors = Object.keys(errors).length > 0;

  const saveDialog = useCallback(() => {
    if (Object.keys(errors).length > 0) return;
    const allowedDraft = filterDraftForPolicy(draft, advisorPrefillPolicy);
    setInviteClientConfig(currentConfig => ({ ...currentConfig, ...allowedDraft }));
    setShowPrefillAnswers(false);
  }, [advisorPrefillPolicy, draft, errors, setInviteClientConfig, setShowPrefillAnswers]);

  const handleDraftChange = useCallback(
    <Field extends keyof PrefillDraft>(field: Field, value: PrefillDraft[Field]) => {
      setPrefillDraftState(currentState => {
        const nextDraft = { ...currentState.answers, [field]: value };

        if (
          field === 'maritalStatus' &&
          allowsMaritalStatus &&
          !shouldAskPartnerQuestions(nextDraft.maritalStatus)
        ) {
          return {
            ...currentState,
            answers: { ...nextDraft, partnerFirstName: '', partnerLastName: '' },
          };
        }

        return { ...currentState, answers: nextDraft };
      });
    },
    [allowsMaritalStatus, setPrefillDraftState],
  );

  const clearDraft = useCallback(() => {
    setPrefillDraftState(createPrefillDraftState());
  }, [setPrefillDraftState]);

  const toggleDraftChoice = useCallback(
    (field: PrefillMultiSelectField, choice: string, checked: boolean) => {
      setPrefillDraftState(currentState => {
        const currentDraft = currentState.answers;
        if (!checked) {
          return {
            ...currentState,
            answers: {
              ...currentDraft,
              [field]: currentDraft[field].filter(v => v !== choice),
            },
          };
        }

        const exclusiveChoices = EXCLUSIVE_PREFILL_CHOICES[MULTI_SELECT_QUESTION_IDS[field]] ?? [];
        if (exclusiveChoices.includes(choice)) {
          return { ...currentState, answers: { ...currentDraft, [field]: [choice] } };
        }

        const kept = currentDraft[field].filter(v => !exclusiveChoices.includes(v));
        return {
          ...currentState,
          answers: { ...currentDraft, [field]: [...kept, choice] },
        };
      });
    },
    [setPrefillDraftState],
  );

  const setBirthDateInput = useCallback(
    (birthDateInput: string) => {
      setPrefillDraftState(currentState => ({ ...currentState, birthDateInput }));
    },
    [setPrefillDraftState],
  );

  const setBirthDateInputValidity = useCallback(
    (isBirthDateInputValid: boolean) => {
      setPrefillDraftState(currentState => ({ ...currentState, isBirthDateInputValid }));
    },
    [setPrefillDraftState],
  );

  return {
    isOpen: showPrefillAnswers,
    draft,
    errors,
    hasErrors,
    birthDateInput: prefillDraftState.birthDateInput,
    prefilledCount,
    draftMissingPlanFields,
    savedMissingPlanFields,
    draftMissingPlanFieldsLabel: formatMissingPlanFields(draftMissingPlanFields),
    savedMissingPlanFieldsLabel: formatMissingPlanFields(savedMissingPlanFields),
    isDraftPlanReady: draftMissingPlanFields.length === 0,
    isSavedPlanReady: savedMissingPlanFields.length === 0,
    advisorPrefillPolicy,
    openDialog,
    closeDialog,
    cancelDialog,
    saveDialog,
    clearDraft,
    setBirthDateInput,
    setBirthDateInputValidity,
    handleDraftChange,
    toggleDraftChoice,
  };
}
